import { memo } from 'react';
import { Box, Typography, Grid, Paper, Button } from '@mui/material';
import { usePricing } from '../../../hooks/usePricing';

function Pricing({ colors, user, scrollToSection }) {
  const { unitPrice, standardPrice, proPrice, standardDeclarations, proDeclarations } = usePricing();
  return (
        <Box id="home-pricing" sx={{ py: { xs: 6, md: 10 } }}>
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 2,
              color: colors.textPrimary,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Tarifs
          </Typography>
          <Typography
            variant="body1"
            sx={{ textAlign: 'center', color: colors.textSecondary, mb: 6, maxWidth: '700px', mx: 'auto' }}
          >
            Choisissez la formule adaptée à votre volume de déclarations et à votre équipe.
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                title: 'Unique',
                price: unitPrice,
                subtitle: 'Paiement à l’unité',
                description: 'Idéal pour déclarer ponctuellement une œuvre. Votre première déclaration est offerte pour tester la plateforme.',
                cta: user ? 'Déclarer maintenant' : 'Commencer',
                href: user ? '/documents' : '/login?redirect=%2Fsettings%23credits',
                featured: false,
              },
              {
                title: 'Standard',
                price: standardPrice,
                subtitle: 'Par mois',
                description: `Parfait pour les utilisateurs réguliers, avec jusqu’à ${standardDeclarations} déclarations par mois.`,
                cta: user ? 'Voir mes déclarations' : 'S’abonner',
                href: user ? '/documents' : '/login?redirect=%2Fsettings%23credits',
                featured: true,
              },
              {
                title: 'Pro',
                price: proPrice,
                subtitle: 'Par mois',
                description: `Pour les gros volumes, avec jusqu’à ${proDeclarations} déclarations par mois.`,
                cta: user ? 'Voir mes déclarations' : 'S’abonner',
                href: user ? '/documents' : '/login?redirect=%2Fsettings%23credits',
                featured: false,
              },
              {
                title: 'Business',
                price: 'Sur devis',
                subtitle: 'Offre personnalisée',
                description: 'Pour les labels, producteurs, équipes et structures qui ont des besoins plus avancés.',
                cta: 'Contacter nous',
                href: '/contact',
                featured: false,
              },
            ].map((tier, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: tier.featured ? 'rgba(129,140,248,0.10)' : colors.bgPaper,
                    border: `1px solid ${tier.featured ? colors.primaryLight : colors.border}`,
                    borderRadius: 3,
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    gap: 3,
                    boxShadow: tier.featured ? '0 12px 30px rgba(129,140,248,0.16)' : 'none',
                  }}
                >
                  <Box>
                    <Typography variant="overline" sx={{ color: colors.primaryLight, fontWeight: 700 }}>
                      {tier.subtitle}
                    </Typography>
                    <Typography variant="h5" sx={{ color: colors.textPrimary, fontWeight: 700, mt: 0.5 }}>
                      {tier.title}
                    </Typography>
                    <Typography variant="h3" sx={{ color: colors.primaryLight, fontWeight: 800, mt: 2 }}>
                      {tier.price}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textSecondary, lineHeight: 1.7, mt: 2 }}>
                      {tier.description}
                    </Typography>
                  </Box>

                  <Button
                    variant={tier.featured ? 'contained' : 'outlined'}
                    component={tier.href.startsWith('#') ? 'button' : 'a'}
                    href={!tier.href.startsWith('#') ? tier.href : undefined}
                    onClick={tier.href.startsWith('#') ? () => scrollToSection(tier.href.replace('#', '')) : undefined}
                    sx={{
                      alignSelf: 'flex-start',
                      borderColor: colors.primaryLight,
                      color: tier.featured ? colors.textInverse : colors.textPrimary,
                      bgcolor: tier.featured ? colors.primaryLight : 'transparent',
                      '&:hover': {
                        bgcolor: tier.featured ? colors.primaryLight : 'rgba(129,140,248,0.08)',
                        borderColor: colors.primaryLight,
                      },
                    }}
                  >
                    {tier.cta}
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
  );
}

export default memo(Pricing);
